import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  DocumentTextIcon,
  KeyIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import { WizardComponentProps } from '../../types';

const MetadataViewer: React.FC<WizardComponentProps> = ({ isEmbedded }) => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();
  const queryClient = useQueryClient();
  const [selectedEntity, setSelectedEntity] = useState<string>('');

  // Fetch metadata
  const { data: metadata, isLoading, error } = useQuery({
    queryKey: ['metadata', customerId, phaseId],
    queryFn: () => apiClient.getMetadata(customerId!, phaseId!),
    enabled: !!(customerId && phaseId)
  });

  // Refresh metadata mutation (all or single entity if selected)
  const refreshMetadataMutation = useMutation({
    mutationFn: () => selectedEntity
      ? apiClient.refreshMetadataEntity(customerId!, phaseId!, selectedEntity)
      : apiClient.refreshMetadata(customerId!, phaseId!),
    onSuccess: (data: any) => {
      toast.success(`Metadata refreshed: ${data.entityCount} entities loaded`);
      queryClient.invalidateQueries({ queryKey: ['metadata', customerId, phaseId] });
      queryClient.invalidateQueries({ queryKey: ['phase-entities', customerId, phaseId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to refresh metadata');
    }
  });

  const handleRefreshMetadata = () => {
    refreshMetadataMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        {!isEmbedded && (
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Metadata Viewer</h1>
              <p className="text-gray-600 mt-2">View SuccessFactors entity and field metadata</p>
            </div>
            <button
              onClick={handleRefreshMetadata}
              disabled={refreshMetadataMutation.isPending}
              className="btn-primary flex items-center"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${refreshMetadataMutation.isPending ? 'animate-spin' : ''}`} />
              {refreshMetadataMutation.isPending ? 'Refreshing...' : 'Refresh Metadata'}
            </button>
          </div>
        )}

        {isEmbedded && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={handleRefreshMetadata}
              disabled={refreshMetadataMutation.isPending}
              className="btn-primary flex items-center"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${refreshMetadataMutation.isPending ? 'animate-spin' : ''}`} />
              {refreshMetadataMutation.isPending ? 'Refreshing...' : 'Refresh Metadata'}
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <ExclamationCircleIcon className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Metadata Available</h3>
          <p className="text-gray-600 mb-6">
            Configure SFSF credentials first, then refresh metadata to load entity information.
          </p>
          <button
            onClick={handleRefreshMetadata}
            disabled={refreshMetadataMutation.isPending}
            className="btn-primary"
          >
            {refreshMetadataMutation.isPending ? 'Loading...' : 'Load Metadata'}
          </button>
        </div>
      </div>
    );
  }

  const entities = metadata ? Object.keys(metadata) : [];
  const selectedEntityData = selectedEntity && metadata ? metadata[selectedEntity] : null;

  return (
    <div className="max-w-9xl mx-auto">
      {!isEmbedded && (
        <>
          {/* Breadcrumb Navigation */}
          <nav className="mb-4 flex items-center text-sm text-gray-600">
            <Link to="/customers" className="hover:text-primary-600 transition-colors">
              Customers
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={`/customers/${customerId}/phases`}
              className="hover:text-primary-600 transition-colors"
            >
              Phases
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={`/customers/${customerId}/phases/${phaseId}`}
              className="hover:text-primary-600 transition-colors"
            >
              Phase Dashboard
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Metadata</span>
          </nav>

          {/* Back Button */}
          <Link
            to={`/customers/${customerId}/phases/${phaseId}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium mb-4 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 mr-1" />
            Back to Phase Dashboard
          </Link>
        </>
      )}

      {refreshMetadataMutation.isPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md text-center">
            <ArrowPathIcon className="w-10 h-10 mx-auto mb-3 text-primary-600 animate-spin" />
            <h2 className="text-lg font-semibold text-gray-900">Fetching data, please do not close this window</h2>
          </div>
        </div>
      )}

      {!isEmbedded ? (
        <div className="mb-12 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Metadata Viewer</h1>
            <p className="text-gray-600 mt-2">View SuccessFactors entity and field metadata</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshMetadata}
              disabled={refreshMetadataMutation.isPending}
              className="btn-secondary flex items-center"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${refreshMetadataMutation.isPending ? 'animate-spin' : ''}`} />
              {refreshMetadataMutation.isPending ? 'Refreshing...' : selectedEntity ? `Refresh ${selectedEntity}` : 'Refresh All Metadata'}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex justify-end">
          <button
            onClick={handleRefreshMetadata}
            disabled={refreshMetadataMutation.isPending}
            className="btn-secondary flex items-center text-sm"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshMetadataMutation.isPending ? 'animate-spin' : ''}`} />
            {refreshMetadataMutation.isPending ? 'Refreshing...' : selectedEntity ? `Refresh ${selectedEntity}` : 'Refresh All'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Entities List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Entities ({entities.length})</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {entities.map((entityName) => (
              <button
                key={entityName}
                onClick={() => setSelectedEntity(entityName)}
                className={`w-full text-left p-3 rounded-md transition-colors ${selectedEntity === entityName
                  ? 'bg-primary-100 text-primary-900 border border-primary-200'
                  : 'hover:bg-gray-100 text-gray-700'
                  }`}
              >
                <div className="flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-3" />
                  <div>
                    <div className="font-medium">{metadata[entityName].label || entityName}</div>
                    <div className="text-sm text-gray-500">
                      {metadata[entityName].fields.length} fields
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Entity Details */}
        <div className="lg:col-span-2">
          {selectedEntityData ? (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {selectedEntityData.label || selectedEntity}
                </h2>
                <p className="text-gray-600">Entity: {selectedEntity}</p>

                {/* Business Keys */}
                {selectedEntityData.businessKeys && selectedEntityData.businessKeys.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Business Keys:</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedEntityData.businessKeys.map((key: string) => (
                        <span
                          key={key}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          <KeyIcon className="w-3 h-3 mr-1" />
                          {key}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Fields Table */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  Fields ({selectedEntityData.fields.length})
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Field Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Label
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Required
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Max Length
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedEntityData.fields.map((field: any) => (
                        <tr key={field.name} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {field.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {field.label || field.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <span className={`px-2 py-1 text-xs rounded-full ${field.dataType === 'string' ? 'bg-blue-100 text-blue-800' :
                              field.dataType === 'number' ? 'bg-green-100 text-green-800' :
                                field.dataType === 'date' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                              }`}>
                              {field.dataType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {field.required ? (
                              <CheckCircleIcon className="w-5 h-5 text-green-600" />
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {field.maxLength || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Entity</h3>
              <p className="text-gray-600">Choose an entity from the list to view its fields and properties</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetadataViewer;
